/**
 * sendDocumentToConversation - Envio de documento (arquivo real) para uma conversa
 *
 * Helper compartilhado extraído de `useChatConversation.handleSendDocument` para que
 * outros pontos do sistema (ex.: compartilhamento de proposta em PDF) reaproveitem
 * exatamente a mesma rota já testada em produção:
 *
 *   1. upload no bucket público `anexos` (pasta Chats/{conversation_id});
 *   2. insert em `messages` (message_type = 'document') + `message_attachments`;
 *   3. envio pelo provider via edge function `send-message` (multi-provider:
 *      Z-API, Notificame, WAHA — o roteamento é feito pela própria função);
 *   4. atualização do status da mensagem (`sent` / `failed`) e registro do erro
 *      no catálogo de erros quando houver falha.
 *
 * Callbacks opcionais permitem que a UI aplique atualizações otimistas sem
 * duplicar a lógica de persistência/envio.
 *
 * CHANGELOG:
 *   RELEASE: 2026-08-12 v1.0.0 - Versão inicial (extração de useChatConversation).
 */

import { supabase } from '@/integrations/supabase/client';
import { registerSendError } from '@/hooks/useErrorCatalog';
import { createLogger } from '@/lib/logger';

const logger = createLogger('sendDocumentToConversation');

/** Dados mínimos da conversa necessários para o envio */
export interface SendDocumentConversation {
  id: string;
  inbox_id: string;
  /** Telefone do contato (1:1) ou identificador externo do grupo */
  phone: string;
  contactId?: string | null;
  providerSlug?: string | null;
}

export interface SendDocumentParams {
  conversation: SendDocumentConversation;
  /** Usuário logado (remetente agente) */
  userId: string;
  /** Arquivo real a ser enviado */
  file: File | Blob;
  /** Nome exibido do arquivo (com extensão) */
  fileName: string;
  /** Mime type; padrão application/octet-stream */
  mimeType?: string;
  /**
   * Conteúdo/legenda da mensagem. Quando omitido, usa `📄 {fileName}`
   * (comportamento histórico do chat).
   */
  caption?: string | null;
  /** Chamado logo após criar a mensagem no banco (para UI otimista) */
  onMessageCreated?: (message: any, attachment: any) => void;
  /** Chamado ao final, com o status definitivo do envio */
  onStatusResolved?: (
    messageId: string,
    status: 'sent' | 'failed',
    externalId: string | null,
    metadata: { providerMessageId: string } | null,
  ) => void;
}

export interface SendDocumentResult {
  messageId: string;
  status: 'sent' | 'failed';
  externalId: string | null;
  publicUrl: string;
}

/**
 * Sobe o arquivo, cria a mensagem e dispara o envio pelo provider da inbox.
 * Lança exceção apenas em falhas de persistência (upload/insert); falha de
 * provider é refletida no status 'failed' retornado.
 */
export async function sendDocumentToConversation(
  params: SendDocumentParams,
): Promise<SendDocumentResult> {
  const {
    conversation,
    userId,
    file,
    fileName,
    mimeType,
    caption,
    onMessageCreated,
    onStatusResolved,
  } = params;

  const resolvedMime = mimeType || (file as File).type || 'application/octet-stream';
  const timestamp = Date.now();
  const extension = fileName.split('.').pop() || 'bin';
  const storagePath = `Chats/${conversation.id}/doc_${timestamp}.${extension}`;

  // PASSO 1: upload no bucket público de anexos do chat
  const { error: uploadError } = await supabase.storage
    .from('anexos')
    .upload(storagePath, file, { contentType: resolvedMime, upsert: false });

  if (uploadError) throw uploadError;

  const { data: publicUrlData } = supabase.storage.from('anexos').getPublicUrl(storagePath);

  // PASSO 2: persiste a mensagem e o anexo
  const { data: messageData, error: insertError } = await supabase
    .from('messages')
    .insert({
      conversation_id: conversation.id,
      sender_type: 'agent',
      sender_id: userId,
      content: caption?.trim() ? caption.trim() : `📄 ${fileName}`,
      message_type: 'document',
      direction: 'outbound',
      status: 'pending',
    })
    .select()
    .single();

  if (insertError) throw insertError;

  const attachmentData = {
    message_id: messageData.id,
    type: 'document' as const,
    url: storagePath,
    mime_type: resolvedMime,
    filename: fileName,
    size_bytes: (file as File).size ?? 0,
  };

  await supabase.from('message_attachments').insert(attachmentData);

  onMessageCreated?.(messageData, attachmentData);

  // PASSO 3: envio pelo provider (abstração multi-provider na edge function)
  const { data: sendResult, error: sendError } = await supabase.functions.invoke('send-message', {
    body: {
      inbox_id: conversation.inbox_id,
      phone: conversation.phone,
      message_type: 'document',
      media_url: publicUrlData.publicUrl,
      media_caption: fileName,
      // Campos obrigatórios para WAHA: mimetype e filename
      media_mime_type: resolvedMime,
      media_filename: fileName,
      // Legenda textual exibida junto ao documento
      content: caption?.trim() || '',
      // Rastreabilidade/observabilidade
      source_type: 'chat',
      message_id: messageData.id,
      conversation_id: conversation.id,
      contact_id: conversation.contactId ?? undefined,
    },
  });

  // PASSO 4: consolida status
  if (sendError || !sendResult?.success) {
    await supabase.from('messages').update({ status: 'failed' }).eq('id', messageData.id);

    registerSendError({
      errorCode: sendResult?.error_code || (sendError as any)?.code || '500',
      provider: conversation.providerSlug || 'unknown',
      inboxId: conversation.inbox_id,
      messageId: messageData.id,
      conversationId: conversation.id,
      messageType: 'document',
      rawError: sendResult || sendError,
      providerMessage: sendResult?.error || (sendError as Error)?.message,
    });

    logger.error('Falha ao enviar documento pelo provider', {
      conversationId: conversation.id,
      messageId: messageData.id,
    });

    onStatusResolved?.(messageData.id, 'failed', null, null);
    return {
      messageId: messageData.id,
      status: 'failed',
      externalId: null,
      publicUrl: publicUrlData.publicUrl,
    };
  }

  const metadata = sendResult.provider_message_id
    ? { providerMessageId: sendResult.provider_message_id as string }
    : null;

  await supabase
    .from('messages')
    .update({
      status: 'sent',
      external_id: sendResult.external_id,
      ...(metadata ? { metadata } : {}),
    })
    .eq('id', messageData.id);

  onStatusResolved?.(messageData.id, 'sent', sendResult.external_id ?? null, metadata);

  return {
    messageId: messageData.id,
    status: 'sent',
    externalId: sendResult.external_id ?? null,
    publicUrl: publicUrlData.publicUrl,
  };
}
